import React, { useEffect, useState, useRef } from "react";
import api from "../api";
import { AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../context/NotificationContext";

const AdminUserManagement = ({ onLogout }) => {
  const { showToast } = useNotification();
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const navigate = useNavigate();

  const usersSectionRef = useRef(null);
  const eventsSectionRef = useRef(null);

  const fetchData = async () => {
    try {
      const uRes = await api.get("/api/auth/users");
      setUsers(uRes.data);
      const eRes = await api.get("/api/events/admin/all");
      setEvents(eRes.data);
    } catch (err) {
      showToast("فشل تحميل البيانات", "danger");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const toggleApproval = async (id, currentStatus) => {
    try {
      await api.patch(`/api/auth/users/${id}/approve`, {
        isApproved: !currentStatus,
      });
      showToast(currentStatus ? "تم إلغاء تفعيل الحساب" : "تم تفعيل الحساب بنجاح", "success");
      fetchData();
    } catch (err) {
      showToast("حصل مشكلة أثناء التعديل", "danger");
    }
  };

  const updatePlan = async (id, plan) => {
    try {
      await api.patch(`/api/auth/users/${id}/plan`, { plan });
      showToast("تم تحديث الباقة بنجاح", "success");
      fetchData();
    } catch (err) {
      showToast("فشل تحديث الباقة", "danger");
    }
  };

  const deleteUser = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا المستخدم نهائياً؟")) return;
    try {
      await api.delete(`/api/auth/users/${id}`);
      showToast("تم حذف المستخدم بنجاح", "info");
      fetchData();
    } catch (err) {
      showToast("فشل حذف المستخدم", "danger");
    }
  };

  const toggleEventApproval = async (id, currentStatus) => {
    try {
      await api.put(`/api/events/${id}`, { isApproved: !currentStatus });
      showToast(currentStatus ? "تم إيقاف الألبوم" : "تم تفعيل الألبوم بنجاح", "success");
      fetchData();
    } catch (err) {
      showToast("حصل مشكلة أثناء التعديل", "danger");
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذه المناسبة نهائياً؟")) return;
    try {
      await api.delete(`/api/events/${id}`);
      showToast("تم حذف المناسبة بنجاح", "info");
      fetchData();
    } catch (err) {
      showToast("فشل حذف المناسبة", "danger");
    }
  };

  return (
    <div className="admin-layout" dir="rtl">
      {/* Header */}
      <header className="admin-header d-flex align-items-center justify-content-between px-3 px-md-4">
        <div className="d-flex align-items-center gap-3 gap-md-4 overflow-hidden">
          <h1
            className="text-xl text-md-2xl font-black text-primary tracking-tighter mb-0 cursor-pointer flex-shrink-0"
            style={{ fontFamily: 'var(--font-headline)', cursor: 'pointer' }}
            onClick={() => navigate("/")}
          >
            يومك
          </h1>
          <nav className="d-flex align-items-center gap-1 gap-md-3 overflow-x-auto text-nowrap hide-scrollbar pb-1 pb-md-0">
            <button
              className="btn btn-link text-decoration-none text-muted fw-bold text-xs text-md-sm p-2 hover:bg-light rounded-3 transition-soft border-0"
              onClick={() => navigate("/admin")}
            >
              المناسبات
            </button>
            <button
              className="btn btn-link text-decoration-none text-primary bg-primary-dim fw-bold text-xs text-md-sm p-2 rounded-3 transition-soft border-0"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              النظام
            </button>
          </nav>
        </div>
        <div className="d-flex align-items-center gap-3">
          <button className="btn btn-light rounded-circle p-2 d-flex align-items-center justify-content-center border-0">
            <span className="material-symbols-outlined fs-5">notifications</span>
          </button>
          <div
            className="user-avatar bg-primary-dim rounded-circle overflow-hidden border-2 border-white shadow-sm d-flex align-items-center justify-content-center cursor-pointer"
            style={{ width: '40px', height: '40px', cursor: 'pointer' }}
            onClick={onLogout}
            title="تسجيل الخروج"
          >
             <span className="material-symbols-outlined text-primary fs-5">logout</span>
          </div>
        </div>
      </header>

      {/* Main Content Canvas */}
      <main className="admin-main pt-24 pb-12 px-4 px-md-8 min-h-screen bg-background">
        <div className="max-w-6xl mx-auto space-y-12">
            {/* Header Section */}
            <div className="row g-4 mb-4">
                <div className="col-lg-8">
                    <span className="text-primary fw-bold tracking-widest uppercase text-xs">نظام الإدارة المركزي</span>
                    <h1 className="display-4 font-black tracking-tight text-on-surface font-headline mt-1">لوحة تحكم الأدمن ✨</h1>
                    <p className="text-muted max-w-2xl leading-relaxed fs-5 arabic-leading">أهلاً بك في لوحة التحكم المركزية. قم بإدارة المستخدمين والفعاليات والألبومات الخاصة بمنصة يومك بكل سهولة وبأعلى مستوى من التحكم.</p>
                </div>
                <div className="col-lg-4">
                    <div className="row g-3">
                       <div className="col-6">
                           <div className="bg-white p-4 rounded-celebration shadow-sm border border-light flex flex-col h-100 text-center">
                               <span className="text-xs font-bold text-muted uppercase tracking-wider mb-1">إجمالي المستخدمين</span>
                               <span className="display-5 font-black text-primary font-headline">{users.length}</span>
                           </div>
                       </div>
                       <div className="col-6">
                           <div className="bg-white p-4 rounded-celebration shadow-sm border border-light flex flex-col h-100 text-center">
                               <span className="text-xs font-bold text-muted uppercase tracking-wider mb-1">الفعاليات النشطة</span>
                               <span className="display-5 font-black text-primary font-headline">{events.filter(e => e.isApproved).length}</span>
                           </div>
                       </div>
                    </div>
                </div>
            </div>

            {/* Section 1: Client Management */}
            <section className="space-y-6" ref={usersSectionRef}>
                <div className="d-flex flex-column flex-md-row items-center justify-content-between mb-4 gap-3">
                    <div className="d-flex items-center gap-3">
                        <div className="w-12 h-12 rounded-celebration bg-primary-dim flex items-center justify-center">
                            <span className="material-symbols-outlined text-primary fs-3">group</span>
                        </div>
                        <h3 className="h3 font-black font-headline mb-0">إدارة المشتركين</h3>
                    </div>
                    <div className="d-flex gap-2 w-100 w-md-auto">
                        <div className="relative w-100" style={{ maxWidth: '400px' }}>
                            <input className="form-control rounded-pill px-4 py-2 text-sm border-0 shadow-sm w-100" placeholder="البحث عن مستخدم..." type="text" />
                            <span className="material-symbols-outlined absolute left-3 top-50 -translate-y-1/2 text-muted text-sm">search</span>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-celebration shadow-lg overflow-hidden border border-light">
                    <div className="table-responsive-md">
                        <table className="table table-hover align-middle mb-0 text-end">
                            <thead className="bg-light">
                                <tr className="border-bottom border-light">
                                    <th className="px-4 py-4 font-headline text-sm font-bold text-muted">المستخدم (الاسم/البريد)</th>
                                    <th className="px-4 py-4 font-headline text-sm font-bold text-muted">بيانات التواصل</th>
                                    <th className="px-4 py-4 font-headline text-sm font-bold text-muted text-center">الباقة</th>
                                    <th className="px-4 py-4 font-headline text-sm font-bold text-muted text-center">الحالة</th>
                                    <th className="px-4 py-4 font-headline text-sm font-bold text-muted text-center">الإجراءات</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {users.map(user => (
                                    <tr key={user._id} className="transition-soft">
                                        <td className="px-4 py-4">
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="w-10 h-10 rounded-circle bg-primary-dim flex items-center justify-center text-primary font-black">
                                                    {user.username.charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <p className="font-black text-sm mb-0">{user.username}</p>
                                                    <p className="text-xs text-muted mb-0">{user.email}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="text-xs space-y-1 fw-bold text-muted">
                                                <p className="d-flex align-items-center gap-2 mb-1"><span className="material-symbols-outlined text-sm">mail</span> {user.email}</p>
                                                <p className="d-flex align-items-center gap-2 mb-0"><span className="material-symbols-outlined text-sm">call</span> {user.phoneNumber}</p>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 text-center">
                                            <select
                                              className="form-select form-select-sm rounded-pill border-0 bg-light text-center fw-bold text-xs"
                                              value={user.plan || 'free'}
                                              onChange={(e) => updatePlan(user._id, e.target.value)}
                                            >
                                              <option value="free">مجانية</option>
                                              <option value="starter">بداية</option>
                                              <option value="pro">احترافية</option>
                                              <option value="corporate">شركات</option>
                                            </select>
                                        </td>
                                        <td className="px-4 py-4 text-center">
                                            <span className={`badge rounded-pill px-3 py-2 fw-bold text-xs ${user.isApproved ? 'bg-success-container text-success' : 'bg-warning-container text-warning'}`}>
                                                {user.isApproved ? 'نشط' : 'قيد المراجعة'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 text-center">
                                            <div className="d-flex justify-content-center gap-2">
                                                <button
                                                    className={`btn btn-sm fw-black px-3 py-2 rounded-pill transition-soft border-0 ${user.isApproved ? 'btn-outline-warning' : 'btn-primary shadow-sm'}`}
                                                    onClick={() => toggleApproval(user._id, user.isApproved)}
                                                >
                                                    {user.isApproved ? "إيقاف الحساب" : "تفعيل الحساب"}
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-danger fw-black px-3 py-2 rounded-pill transition-soft"
                                                    onClick={() => deleteUser(user._id)}
                                                >
                                                    حذف نهائي
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* Section 2: Event Management (Albums) */}
            <section className="space-y-6 mt-10" ref={eventsSectionRef}>
                <div className="d-flex flex-column flex-md-row items-center justify-content-between mb-4 gap-3">
                    <div className="d-flex items-center gap-3">
                        <div className="w-12 h-12 rounded-celebration bg-primary-dim flex items-center justify-center">
                            <span className="material-symbols-outlined text-primary fs-3">photo_album</span>
                        </div>
                        <h3 className="h3 font-black font-headline mb-0">إدارة المناسبات والألبومات</h3>
                    </div>
                    <button
                        className="btn btn-link text-primary fw-black text-decoration-none d-flex align-items-center gap-2"
                        onClick={() => navigate("/admin")}
                    >
                        عرض جميع الفعاليات
                        <span className="material-symbols-outlined fs-5">arrow_back</span>
                    </button>
                </div>
                <div className="bg-white rounded-celebration shadow-lg overflow-hidden border border-light">
                    <div className="table-responsive-md">
                        <table className="table table-hover align-middle mb-0 text-end">
                            <thead className="bg-light">
                                <tr className="border-bottom border-light">
                                    <th className="px-4 py-4 font-headline text-sm font-bold text-muted">اسم الفعالية (الألبوم)</th>
                                    <th className="px-4 py-4 font-headline text-sm font-bold text-muted">مالك الفعالية</th>
                                    <th className="px-4 py-4 font-headline text-sm font-bold text-muted text-center">الحالة</th>
                                    <th className="px-4 py-4 font-headline text-sm font-bold text-muted text-center">الإجراءات</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {events.map(ev => (
                                    <tr key={ev._id} className="transition-soft">
                                        <td className="px-4 py-4">
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="w-12 h-12 rounded-celebration bg-light overflow-hidden shrink-0 d-flex align-items-center justify-content-center border">
                                                    <span className="material-symbols-outlined text-primary">
                                                      {ev.name.includes("زفاف") || ev.name.includes("فرح") ? "celebration" :
                                                       ev.name.includes("تخرج") ? "school" :
                                                       ev.name.includes("عيد ميلاد") ? "cake" : "event"}
                                                    </span>
                                                </div>
                                                <div>
                                                    <p className="font-black text-sm mb-0">{ev.name}</p>
                                                    <p className="text-xs text-muted mb-0">كود الفعالية: {ev._id.slice(-8).toUpperCase()}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="d-flex align-items-center gap-2 fw-bold text-muted">
                                                <div className="w-6 h-6 rounded-circle bg-primary-dim flex items-center justify-center text-[10px] text-primary font-black">
                                                    {ev.owner?.charAt(0).toUpperCase()}
                                                </div>
                                                <span className="text-sm">{ev.owner}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 text-center">
                                            <span className={`badge rounded-pill px-3 py-2 fw-bold text-xs ${ev.isApproved ? 'bg-success-container text-success' : 'bg-warning-container text-warning'}`}>
                                                {ev.isApproved ? 'نشط' : 'قيد المراجعة'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 text-center">
                                            <div className="d-flex justify-content-center gap-2">
                                                <button
                                                    className={`btn btn-sm fw-black px-3 py-2 rounded-pill transition-soft border-0 ${ev.isApproved ? 'btn-outline-warning' : 'btn-primary shadow-sm'}`}
                                                    onClick={() => toggleEventApproval(ev._id, ev.isApproved)}
                                                >
                                                    {ev.isApproved ? "إيقاف الألبوم" : "تفعيل الألبوم"}
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-danger fw-black px-3 py-2 rounded-pill transition-soft"
                                                    onClick={() => deleteEvent(ev._id)}
                                                >
                                                    حذف نهائي
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>
        </div>
      </main>

      {/* Footer Anchor */}
      <footer className="w-full border-t border-slate-100 bg-white mt-auto">
        <div className="container-fluid px-4 py-4">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
            <p className="text-xs text-muted mb-0">© {new Date().getFullYear()} يومك – Curator Dashboard. جميع الحقوق محفوظة.</p>
            <div className="d-flex gap-4">
                <span className="text-xs text-muted">Terms of Service</span>
                <span className="text-xs text-muted">Privacy Policy</span>
                <span className="text-xs text-muted">Internal Documentation</span>
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default AdminUserManagement;
